import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Modal, Form, notification, Row, Input, Col, Radio, Button, Upload, Select } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useI18nLocalization } from '../../../i18nLocalization/useI18nLocalization';
import { useApplicationsLogic } from '../../useApplicationsLogic';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { useUtilities } from '../../../utilities/utils';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { applicationName, applicationDescription } from '../../../validation/regex';
import './style.less';

const EditApplicationModal = ({ isVisible, onSaveCallback, onCloseCallback, application, appNameList, categories }) => {
	dayjs.extend(utc);

	const applicationLogic = useApplicationsLogic();
	const utilities = useUtilities();

	const { t } = useI18nLocalization();
	const [isSaving, setIsSaving] = useState(false);
	const [form] = Form.useForm();
	const [areFormErrors, setAreFormErrors] = useState(true);
	const [appEnabled, setAppEnabled] = useState(application.enabled);
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [iconFile, setIconFile] = useState(null);
	const [logoFile, setLogoFile] = useState(null);
	const [displayColorPicker, setDisplayColorPicker] = useState(false);
	const [color, setColor] = useState(null);

	const { TextArea } = Input;
	const { Option } = Select;

	useEffect(() => {
		form.setFieldsValue({
			appEnabledBoolean: application.enabled,
			appName: application.name,
			appDescription: application.description,
			appUrl: application.url,
			//icon //-- icon/logo isn't displayed, will only change if new one is uploaded
			//logo
			appSupportUrl: application.supportUrl,
			appColor: application.color,
			appInternalBoolean: application.internal,
			appCategory: application.categoryId
		});
		setColor(application.color);
	}, [JSON.stringify(application)]);

	const handleOnCancel = () => {
		setIsSaving(false);
		clearForm();
		onCloseCallback();
	};

	const getUpdatedAplication = async (formValues) => {
		const newApplicationObject = {
			enabled: appEnabled,
			name: formValues.appName,
			description: formValues.appDescription,
			url: formValues.appUrl,
			icon: iconFile
				? await utilities.convertToBase64(iconFile).then(function (result) {
						return result;
				  })
				: application.icon,
			logo: logoFile
				? await utilities.convertToBase64(logoFile).then(function (result) {
						return result;
				  })
				: application.logo,
			supportUrl: formValues.appSupportUrl,
			color: formValues.appColor,
			id: application.id,
			categoryId: formValues.appCategory
		};
		return newApplicationObject;
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);

		const updatedApplication = await getUpdatedAplication(formValues);
		const successful = await applicationLogic.updateApplication(updatedApplication);
		if (successful) {
			notification['success']({
				message: `${application.name} ` + t('Updated') + '!',
				duration: 5
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t('ApplicationUpdateError'),
				duration: 10
			});
		}

		setIsSaving(false);
	};

	const handleDelete = async () => {
		setIsSaving(true);

		const applicationToDeleteId = application.id;
		const successful = await applicationLogic.deleteApplication({ id: applicationToDeleteId });
		if (successful) {
			notification['success']({
				message: `${application.name} ` + t('Deleted')
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t('ApplicationUpdateError'),
				duration: 10
			});
		}

		setIsSaving(false);
	};

	const clearForm = () => {
		form.resetFields();
	};

	const enabledChange = (e) => {
		setAppEnabled(e.target.value);
	};

	const handleColorPick = () => {
		setDisplayColorPicker(!displayColorPicker);
	};

	const handleColorChangeComplete = (color) => {
		setDisplayColorPicker(color);
		setColor(color);
		form.setFieldsValue({
			appColor: color
		});
	};

	return (
		<>
			<Modal
				title={t(`Edit ${application.name}`)}
				okText={t('Save')}
				visible={isVisible}
				onOk={() => {
					form.submit();
				}}
				okButtonProps={{ disabled: areFormErrors }}
				onCancel={handleOnCancel}
				cancelText={t('Cancel')}
				confirmLoading={isSaving}
				forceRender
				footer={[
					<Button
						type='danger'
						key='delete'
						style={{ float: 'left' }}
						onClick={() => {
							setDeleteModalVisible(true);
						}}
					>
						{t('Delete')}
					</Button>,
					<Button key='cancel' onClick={handleOnCancel}>
						{t('Cancel')}
					</Button>,
					<Button
						key='save'
						onClick={() => {
							form.submit();
						}}
					>
						{t('Save')}
					</Button>
				]}
			>
				<ConfirmDeleteModal
					deleteModalIsVisible={deleteModalVisible}
					onConfirmDeleteCallback={() => {
						handleDelete();
						setDeleteModalVisible(false);
						onSaveCallback();
					}}
					onCancelDeleteCallback={() => {
						setDeleteModalVisible(false);
					}}
					applicationName={application.name}
				></ConfirmDeleteModal>

				<Form
					form={form}
					onFieldsChange={() => {
						const areErrors = form.getFieldsError().some((fe) => fe.errors.length > 0);
						setAreFormErrors(areErrors);
					}}
					layout='vertical'
					requiredMark
					scrollToFirstError
					onFinish={handleOnSave}
				>
					<Row>
						<Col span={24}>
							<Form.Item
								label={t('Application Name')}
								name='appName'
								rules={[
									{
										validator: (field, value) => {
											if (!value) {
												return Promise.reject(new Error(t('Name is required')));
											}
											if (value != application.name && appNameList.includes(value)) {
												return Promise.reject(new Error(t('Name cannot be used by another application')));
											}
											if (!value.match(applicationName)) {
												return Promise.reject(new Error(t('Name must contain only letters, numbers, and symbols')));
											}
											return Promise.resolve();
										}
									}
								]}
							>
								<Input placeholder={t('application name')} />
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<Form.Item label={t('What category is this application?')} name='appCategory'>
								<Select placeholder={t('Category') + '?'}>
									{_.map(categories, (category) => {
										return (
											<Option value={category.id} key={category.id}>
												{category.name}
											</Option>
										);
									})}
								</Select>
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<Form.Item
								label={t('Description')}
								name='appDescription'
								rules={[
									{
										validator: (field, value) => {
											if (!value) {
												return Promise.reject(new Error(t('Description is required')));
											}
											if (!value.match(applicationDescription)) {
												return Promise.reject(
													new Error(t('Description must contain only letters, numbers, and symbols'))
												);
											}
											return Promise.resolve();
										}
									}
								]}
							>
								<TextArea
									maxLength={70}
									showCount
									placeholder={t('short description of what this app does (70 chars or less)')}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<Form.Item
								label={t('Url')}
								name='appUrl'
								rules={[
									{
										validator: (field, value) => {
											if (!value) {
												return Promise.reject(new Error(t('Url is required')));
											}
											return Promise.resolve();
										}
									}
								]}
							>
								<Input placeholder={t('the url of the application (must start with http or https)')} />
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<Form.Item label={t('EntDev HelpDesk Support Link (Url)')} name='appSupportUrl'>
								<Input placeholder={t('url to the EntDev HelpDesk for this application')} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[6, 0]}>
						<Col span={12}>
							<Form.Item
								label={t('Icon')}
								name='appIcon'
								rules={[
									{
										validator: (field, value) => {
											if (
												value &&
												value.file &&
												value.file.status !== 'removed' &&
												value.file.type !== 'image/png' &&
												value.file.type !== 'image/jpeg'
											) {
												value.file = null;
												setIconFile(null);
												return Promise.reject(new Error(t('Only PNGs and JPG(E)s are allowed')));
											}
											return Promise.resolve();
										}
									}
								]}
							>
								<Upload
									accept='image/png, image/jpeg'
									multiple={false}
									fileList={iconFile ? [].concat(iconFile) : []}
									beforeUpload={() => {
										return false;
									}}
									onChange={(info) => {
										let file = info.file;

										if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
											setIconFile(file);
										} else {
											setIconFile(null);
										}
									}}
									onRemove={() => {
										setIconFile(null);
									}}
								>
									<Button icon={<UploadOutlined />}>{t('Click to Upload')}</Button>
								</Upload>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label={t('Logo')}
								name='appLogo'
								rules={[
									{
										validator: (field, value) => {
											if (
												value &&
												value.file &&
												value.file.status !== 'removed' &&
												value.file.type !== 'image/png' &&
												value.file.type !== 'image/jpeg'
											) {
												value.file = null;
												setIconFile(null);
												return Promise.reject(new Error(t('Only PNGs and JPG(E)s are allowed')));
											}
											return Promise.resolve();
										}
									}
								]}
							>
								<Upload
									accept='image/png, image/jpeg'
									multiple={false}
									fileList={logoFile ? [].concat(logoFile) : []}
									beforeUpload={() => {
										return false;
									}}
									onChange={(info) => {
										let file = info.file;

										if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
											setLogoFile(file);
										} else {
											setLogoFile(null);
										}
									}}
									onRemove={() => {
										setLogoFile(null);
									}}
								>
									<Button icon={<UploadOutlined />}>{t('Click to Upload')}</Button>
								</Upload>
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<Form.Item label={t('Should the application be enabled?')} name='appEnabledBoolean'>
								<Radio.Group onChange={enabledChange} value={appEnabled}>
									<Radio value={true}>{t('Enabled')}</Radio>
									<Radio value={false}>{t('Disabled')}</Radio>
								</Radio.Group>
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<Form.Item label={t('Application color')} name='appColor'>
								{!displayColorPicker ? (
									<Input
										placeholder={t('hex code')}
										onClick={handleColorPick}
										style={{ width: '65%' }}
										autoComplete='off'
									></Input>
								) : (
									<Input disabled placeholder={t('hex code')} style={{ width: '50%' }} autoComplete='off'></Input>
								)}
							</Form.Item>
						</Col>
					</Row>
				</Form>
				{displayColorPicker ? (
					<div
						className='color-picker'
						style={{
							position: 'absolute',
							zIndex: '2',
							top: '61%',
							left: '55%'
						}}
					>
						<HexColorPicker color={color} onChange={handleColorChangeComplete} />
						<HexColorInput
							color={color}
							onChange={handleColorChangeComplete}
							style={{ marginLeft: '10%', zIndex: '2' }}
							autoFocus
						/>
					</div>
				) : null}
				{displayColorPicker && (
					<div
						style={{ position: 'fixed', top: '0px', right: '0px', bottom: '0px', left: '0px' }}
						onClick={() => {
							setDisplayColorPicker(false);
						}}
					></div>
				)}
			</Modal>
		</>
	);
};

EditApplicationModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	onSaveCallback: PropTypes.func.isRequired,
	onCloseCallback: PropTypes.func.isRequired,
	application: PropTypes.object,
	appNameList: PropTypes.array.isRequired,
	categories: PropTypes.array.isRequired
};

export default EditApplicationModal;
